import { ChevronRight, Sparkles, GraduationCap, FileText } from "lucide-react";
import { useState } from "react";
import { Section } from "@/components/dashboard/Section";
import { formatEventTitle } from "@/lib/utils";
import PracticeExamModal from "@/components/PracticeExamModal";

const formatDateLong = (d) => {
  const m = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${d.getDate()} ${m[d.getMonth()]}`;
};

export function HomeworkSection({ homework, setEventModalId, onQuiz }) {
  const [examEvent, setExamEvent] = useState(null);
  const horizon = new Date();
  horizon.setDate(horizon.getDate() + 14);
  const items2w = homework.filter((e) => {
    try { return new Date(e.start) <= horizon; } catch { return false; }
  });
  if (items2w.length === 0) return null;
  return (
    <Section title="Läxor & Prov (2 v)" to="/laxor" testid="homework-section">
      <ul className="space-y-2.5 stagger">
        {items2w.map((e) => {
          const ai = e.ai_auto_applied;
          const suggestion = e.ai_suggested_category && e.ai_suggested_category !== e.category;
          return (
            <li
              key={e.id}
              className="card-soft p-3.5 fade-up cursor-pointer hover:bg-[color:var(--bg-elevated)] transition-colors"
              data-testid={`homework-item-${e.id}`}
              onClick={() => setEventModalId(e.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 ${
                    e.category === "test"
                      ? "bg-[#FDF2EE] text-[#E07A5F]"
                      : e.category === "assignment"
                      ? "bg-[#E7F4F1] text-[#2A9D8F]"
                      : "bg-[#F0F6E6] text-[color:var(--moss)]"
                  }`}
                >
                  {e.category === "test" ? "Prov" : e.category === "assignment" ? "Uppgift" : "Läxa"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate flex items-center gap-1.5">
                    {formatEventTitle(e)}
                    {ai && (
                      <span
                        className="text-[9px] font-semibold text-[color:var(--jarvis)] flex items-center gap-0.5"
                        title="Auto-uppgraderad av Edit"
                        data-testid={`homework-item-ai-${e.id}`}
                      >
                        <Sparkles size={9} /> AI
                      </span>
                    )}
                    {suggestion && (
                      <span
                        className="text-[9px] font-semibold text-[#E07A5F] flex items-center gap-0.5"
                        title="Edit har ett förslag"
                        data-testid={`homework-item-suggest-${e.id}`}
                      >
                        <Sparkles size={9} /> förslag
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-[color:var(--text-secondary)] mt-0.5">
                    {formatDateLong(new Date(e.start))}
                    {e.feed_owner && ` · ${e.feed_owner}`}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <ChevronRight size={14} className="text-[color:var(--text-tertiary)] mt-1" />
                  {onQuiz && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); onQuiz(e); }}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--jarvis)] text-white hover:opacity-90 flex items-center gap-1 font-medium transition-opacity"
                      data-testid={`quiz-me-dash-${e.id}`}
                    >
                      <GraduationCap size={10} /> Förhör mig
                    </button>
                  )}
                  {e.exam_material && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); setExamEvent(e); }}
                      className="text-[10px] px-2 py-0.5 rounded-full border border-[color:var(--jarvis)] text-[color:var(--jarvis)] hover:bg-[color:var(--jarvis)] hover:text-white flex items-center gap-1 font-medium transition-colors"
                      data-testid={`practice-exam-dash-${e.id}`}
                    >
                      <FileText size={10} /> Övningsprov
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {examEvent && (
        <PracticeExamModal
          eventId={examEvent.id}
          eventTitle={formatEventTitle(examEvent)}
          onClose={() => setExamEvent(null)}
        />
      )}
    </Section>
  );
}

export function StudyPlanCard({ studyPlans, setStudyPlanOpen }) {
  if (studyPlans.length === 0) return null;
  return (
    <button
      onClick={() => setStudyPlanOpen(true)}
      className="w-full mt-5 rounded-2xl p-4 text-left transition-transform active:scale-[0.99] fade-up"
      style={{
        background: "linear-gradient(135deg, rgba(61,90,128,0.07) 0%, rgba(61,90,128,0.01) 100%)",
        border: "1px solid rgba(61,90,128,0.18)",
      }}
      data-testid="study-plan-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/70 text-[color:var(--jarvis)] flex items-center justify-center flex-shrink-0">
          <Sparkles size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-xs font-semibold text-[color:var(--jarvis)] tracking-wide">STUDIESCHEMA</span>
            <span className="text-[10px] text-[color:var(--text-tertiary)]">
              · {studyPlans.length === 1 ? studyPlans[0].user_name : `${studyPlans.length} barn`}
            </span>
          </div>
          <ul className="space-y-1.5">
            {studyPlans.map((plan) => {
              const todayIso = new Date().toISOString().slice(0, 10);
              const next = (plan.sessions || []).find((s) => s.date >= todayIso);
              return (
                <li key={plan.user_id} className="text-sm leading-snug" data-testid={`study-plan-card-${plan.user_name}`}>
                  <span className="font-medium text-[color:var(--text-primary)]">
                    {plan.user_name}:
                  </span>{" "}
                  {next ? (
                    <span className="text-[color:var(--text-secondary)]">
                      {next.date === todayIso ? "Idag" : next.date} · {next.duration_min}m · {next.test_title}
                    </span>
                  ) : (
                    <span className="text-[color:var(--text-tertiary)]">
                      {plan.test_count} prov · alla pass klara
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-[color:var(--jarvis)] font-medium mt-2 flex items-center gap-1">
            Öppna schema <ChevronRight size={11} />
          </p>
        </div>
      </div>
    </button>
  );
}
